// Customer Portal SPA routes.
// See PORTAL_SEPARATION_INVENTORY.md §4 for the staged route plan and
// docs/specs/PORTAL_AUTHZ_AUTHN_SSOT.md for the auth model.

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { OrderStatusPage } from './pages/OrderStatusPage';
import { PurchaseRequestDetailPage } from './pages/PurchaseRequestDetailPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { ImportGuidePage } from './pages/ImportGuidePage';
import { InvoicesPage } from './pages/InvoicesPage';
import { LoginPage } from './pages/LoginPage';
import { LoginVerifyPage } from './pages/LoginVerifyPage';
import { ShipmentDetailPage } from './pages/ShipmentDetailPage';
import { ShipmentsPage } from './pages/ShipmentsPage';
import { StockFavoritesPage } from './pages/StockFavoritesPage';
import { SupportPage } from './pages/SupportPage';
import { SupportThreadPage } from './pages/SupportThreadPage';
import { VehiclesPage } from './pages/VehiclesPage';
import { VehicleDetailPage } from './pages/VehicleDetailPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RequireAuth } from './components/RequireAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ExternalRedirect } from './components/ExternalRedirect';
import { STOCK_HREF } from './components/chrome/chromeNav';
import { STOCK_HANDOFF_PATH } from './lib/stockSession';
import { StockHandoffPage } from './pages/StockHandoffPage';
import { LogoutPage } from './pages/LogoutPage';

export function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/verify" element={<LoginVerifyPage />} />
          {/* Sign-in / sign-out shared with the Stock site (lib/stockSession.ts):
            /stock-handoff opens Stock signed in as this user, /logout is where
            Stock's logout ends the portal session too. */}
          <Route path={STOCK_HANDOFF_PATH} element={<StockHandoffPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          {/* CP-only self-registration is disabled: AUCNET Stock (AS) is the
            sole member-registration entry and CP accounts are auto-provisioned
            from AS (AS-primary pivot 2026-07-01). The former 4-step signup UI
            (/register, /register/step2, /register/verify, /register/complete)
            and the legacy /signup path all redirect to /login so bookmarks and
            emailed links keep working without exposing a CP signup form. The
            Signup* page components remain in the tree but are no longer routed;
            re-enable here once the AS→CP provisioning contract is intentionally
            replaced. */}
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/register/*" element={<Navigate to="/login" replace />} />
          {/* Phase 4 Stream I — invitation acceptance landing page. The token
            in the URL path IS the credential; no RequireAuth wrapper and no
            AppShell (the user has no session yet). The matching BFF route
            lives at /api/portal/invite/:token. */}
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          {/* Legacy public-catalog paths. The in-portal Stock browse (StockListPage)
            was removed, but `/cars-for-sale` and `/stock` remain bookmarked and are
            the header "Stock" link target. Without these routes React Router matched
            nothing → empty `#root` (white screen). They now hard-redirect to the
            external public Stock site. This is a cross-origin redirect, so a
            react-router <Navigate> cannot be used — ExternalRedirect does a
            window.location.replace and still renders a "Redirecting…" frame so the
            root is never empty. */}
          <Route path="/cars-for-sale" element={<ExternalRedirect url={STOCK_HREF} />} />
          <Route path="/stock" element={<ExternalRedirect url={STOCK_HREF} />} />
          {/* Root → the authenticated landing page. The public Stock browse
            catalog (`/cars-for-sale`, `/stock`) was removed: the portal now
            shows only the signed-in screens that carry the left-side menu.
            RequireAuth on /orders bounces anonymous visitors to /login, so
            /login is the effective entry point. `/stock/favorites` stays (it
            is an authenticated left-menu screen). */}
          <Route path="/" element={<Navigate to="/orders" replace />} />
          {/* 注文落札状況 — phase-1 landing page (replaces the generic
            Dashboard). DashboardPage is retained in the tree but no longer
            routed; see docs / sidebar plan. */}
          <Route
            path="/orders"
            element={
              <RequireAuth>
                <AppShell>
                  <OrderStatusPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Purchase request detail — under /orders so the Orders nav item stays
            active. Cards on /orders link here. */}
          <Route
            path="/orders/requests/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <PurchaseRequestDetailPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* `/stock/favorites` (authenticated) — keep BEFORE any `/stock/:id`
            ambiguity is moot since it is its own exact path. Favorites is a
            logged-in feature so it stays inside RequireAuth + AppShell. */}
          <Route
            path="/stock/favorites"
            element={
              <RequireAuth>
                <AppShell>
                  <StockFavoritesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 2 Stream C — shipments list + detail. */}
          <Route
            path="/shipments"
            element={
              <RequireAuth>
                <AppShell>
                  <ShipmentsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/shipments/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <ShipmentDetailPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 2 Stream B — vehicles list + detail. */}
          <Route
            path="/vehicles"
            element={
              <RequireAuth>
                <AppShell>
                  <VehiclesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/vehicles/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <VehicleDetailPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 2 Stream D — invoices list. */}
          <Route
            path="/invoices"
            element={
              <RequireAuth>
                <AppShell>
                  <InvoicesPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 3 Stream G — support: new-message form + the customer's conversations. */}
          <Route
            path="/support"
            element={
              <RequireAuth>
                <AppShell>
                  <SupportPage />
                </AppShell>
              </RequireAuth>
            }
          />
          <Route
            path="/support/messages/:id"
            element={
              <RequireAuth>
                <AppShell>
                  <SupportThreadPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 3 Stream E — documents list + signed-URL download. */}
          <Route
            path="/documents"
            element={
              <RequireAuth>
                <AppShell>
                  <DocumentsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Recommendations are intentionally hidden until the feature is complete. */}
          <Route path="/recommendations" element={<Navigate to="/orders" replace />} />
          {/* Phase 4 Stream J — static "Import Conditions Guide". The PSM
            source for this page was a "ghost page" (file existed but no
            route registered); customer-portal explicitly wires it here. */}
          <Route
            path="/guide/import"
            element={
              <RequireAuth>
                <AppShell>
                  <ImportGuidePage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Phase 4 Stream H — account settings (profile + notifications + locale). */}
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AppShell>
                  <AccountSettingsPage />
                </AppShell>
              </RequireAuth>
            }
          />
          {/* Catch-all 404 — any path not matched above renders a real page (with
            chrome + links back) instead of a blank `#root`. Keep this LAST so all
            explicit routes win first. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
